/**
 * canvas-engine/renderer/elements/IconElement.jsx
 *
 * OWN per-type element renderer for `icon` elements.
 *
 * ZERO REUSE: imports NOTHING from template-builder/ or academy-ui/src/template/.
 *
 * Inner-content vs positioning CONTRACT: renders ONLY the inner icon, filling
 * its positioned box. The parent CanvasRenderer owns positioning. DOM output is
 * identical in builder and reader modes. (Req 4.2, 17.1)
 *
 * Renders an icon image (resolved via OWN `assetRef`) or a simple glyph/text
 * fallback. Pure / presentational.
 */

import React from 'react';
import { assetRef } from '../../model/assetRef';

function str(v) {
  return v == null ? '' : String(v);
}

function color(v, fallback) {
  return typeof v === 'string' && v.length > 0 ? v : fallback;
}

function num(v, fallback) {
  return Number.isFinite(v) ? v : fallback;
}

/**
 * Render an icon image or simple glyph.
 *
 * @param {object} props
 * @param {object} props.element normalized `icon` Canvas_Element
 */
export default function IconElement({ element }) {
  const content = (element && element.content) || {};
  const style = (element && element.style) || {};

  const asset = assetRef(content.asset);
  const src = asset.displayLink || asset.storedLink || '';
  const alt = str(content.alt) || 'Icon';
  const glyph = str(content.glyph) || str(content.symbol);

  const rootStyle = {
    width: '100%',
    height: '100%',
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: color(style.background, 'transparent'),
    borderRadius: num(style.radius, 0),
    overflow: 'hidden',
    color: color(style.color, '#0f172a'),
  };

  return (
    <div className="ce-icon" style={rootStyle}>
      {src ? (
        <img
          className="ce-icon__img"
          src={src}
          alt={alt}
          style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block' }}
        />
      ) : (
        <span className="ce-icon__glyph" aria-label={alt} style={{ fontSize: num(style.iconSize, 24), lineHeight: 1 }}>
          {glyph || '★'}
        </span>
      )}
    </div>
  );
}
